/**
 * PATH: packages/email-hub/src/feature/pages/inbox-page/InboxCommandSurface.tsx
 * FILE_ID: EMAIL-INBOX-COMMAND-SURFACE-001
 * PURPOSE: Render a stable two-row command surface for every Inbox width.
 * WHY: Selection replaces search; refinement never adds permanent rows.
 * LANDMINE: The pill strip is the ONLY place the operator can see which
 *      filters are narrowing the list. It was built on 2026-07-06 and then
 *      left unmounted by the command-surface rewrite, leaving a bare count
 *      badge as the whole answer to "why am I not seeing my mail". It
 *      renders null at rest, so it costs no row until a filter is on — do
 *      not "reclaim" it as chrome.
 * LANDMINE: Select-all ("All") lives in BulkCommandRow, not the search row.
 *      It is only reachable once something is selected, which is fine
 *      BECAUSE every inbox row renders its own checkbox (EmailRow,
 *      selectable defaults true, opacity-40 at rest). If row selection is
 *      ever made hover-only or removed, bulk mode becomes unreachable and
 *      this control has to come back to the search row.
 */

import { Briefcase, Undo2 } from 'lucide-react'

import { InboxFilterPills } from '../../components/InboxFilterPills'
import { InboxListToolbar } from '../../components/InboxListToolbar'
import { deriveInboxScopeCounts } from '../../components/inboxScopeCounts'
import {
  isEveryLoadedEmailSelected,
  selectEveryLoadedEmail,
} from '../../components/inbox-command/contracts'
import { useBinnedRestoreAll } from './controller/useBinnedRestoreAll'
import type { InboxPageController } from './useInboxPageController'
import { SearchCommandRow } from './SearchCommandRow'

/** Binned: everything here is already binned, so the terminal verbs become
 *  one reversible Restore. Nothing on this folder bins or hard-deletes. */
function RestoreAllAction({ vm }: { vm: InboxPageController }) {
  const { enabled, restoringAll, count, restoreAll } = useBinnedRestoreAll(vm)
  if (!enabled) return null
  return (
    <button
      type="button"
      onClick={() => { void restoreAll() }}
      disabled={restoringAll || count === 0}
      title="Restore every loaded binned email to the inbox"
      data-testid="binned-restore-all"
      className="inline-flex h-8 shrink-0 items-center gap-1 rounded-md border border-border px-2 text-xs text-muted-foreground transition-colors hover:border-emerald-300 hover:bg-emerald-500/10 hover:text-emerald-600 disabled:opacity-50 disabled:hover:bg-transparent dark:hover:text-emerald-300"
    >
      <Undo2 className="h-3.5 w-3.5" aria-hidden />
      <span>{restoringAll ? 'Restoring\u2026' : 'Restore all'}</span>
    </button>
  )
}

function BulkCommandRow({ vm }: { vm: InboxPageController }) {
  const loadedIds = vm.dedupedEmails.map((email) => email.id)
  const everyLoadedSelected = isEveryLoadedEmailSelected(
    vm.selectedIds,
    loadedIds,
  )
  const binned = vm.folder === 'binned'
  return (
    <div
      className="flex h-8 min-w-0 flex-1 items-center gap-1 overflow-x-auto overscroll-x-contain rounded-md bg-primary/5 px-1"
      data-testid="inbox-bulk-command-row"
    >
      <span className="shrink-0 px-1 text-xs font-semibold text-primary">
        {vm.selectedIds.size} selected
      </span>
      {!everyLoadedSelected && loadedIds.length > 0 && (
        <button
          type="button"
          onClick={() => vm.setSelectedIds(selectEveryLoadedEmail(loadedIds))}
          aria-label={`Select all ${loadedIds.length} loaded emails`}
          title={`Select all ${loadedIds.length} loaded emails`}
          className="shrink-0 rounded px-2 py-1 text-[10px] font-semibold text-primary hover:bg-primary/10"
          data-testid="inbox-select-loaded"
        >
          All
        </button>
      )}
      <button
        type="button"
        onClick={() => vm.setBulkLinkOpen(true)}
        className="inline-flex shrink-0 items-center gap-1 rounded px-2 py-1 text-[10px] text-blue-700 hover:bg-blue-500/10 dark:text-blue-300"
      >
        <Briefcase className="h-3 w-3" />
        Link
      </button>
      {binned ? (
        <button
          type="button"
          onClick={() => vm.handleBulkAction('restore')}
          className="shrink-0 rounded px-2 py-1 text-[10px] text-emerald-700 hover:bg-emerald-500/10 dark:text-emerald-300"
          data-testid="inbox-bulk-restore"
        >
          Restore
        </button>
      ) : (
        <>
          <button
            type="button"
            onClick={() => vm.handleBulkAction('done')}
            className="shrink-0 rounded px-2 py-1 text-[10px] text-emerald-700 hover:bg-emerald-500/10 dark:text-emerald-300"
          >
            Done
          </button>
          <button
            type="button"
            onClick={() => vm.handleBulkAction('trash')}
            className="shrink-0 rounded px-2 py-1 text-[10px] text-red-700 hover:bg-red-500/10 dark:text-red-300"
          >
            Junk
          </button>
        </>
      )}
      <button
        type="button"
        onClick={() => vm.handleBulkAction('star')}
        className="shrink-0 rounded px-2 py-1 text-[10px] hover:bg-accent"
      >
        Star
      </button>
      <button
        type="button"
        onClick={() => vm.handleBulkAction('read')}
        className="shrink-0 rounded px-2 py-1 text-[10px] hover:bg-accent"
      >
        Read
      </button>
      <button
        type="button"
        onClick={() => vm.handleBulkAction('unread')}
        className="shrink-0 rounded px-2 py-1 text-[10px] hover:bg-accent"
      >
        Unread
      </button>
      <button
        type="button"
        onClick={() => vm.setSelectedIds(new Set())}
        className="ml-auto shrink-0 rounded px-2 py-1 text-[10px] text-muted-foreground hover:bg-accent"
      >
        Clear
      </button>
    </div>
  )
}

export function InboxCommandSurface({ vm }: { vm: InboxPageController }) {
  const scopeCounts = deriveInboxScopeCounts(vm.stats)
  return (
    <div className="border-b border-border bg-background/80 px-2 py-2 backdrop-blur-sm">
      <InboxListToolbar
        isInboxFolder={vm.isInboxFolder}
        folderTitle={vm.folderTitle}
        showScopeBar={
          vm.isInboxFolder
          || vm.folder === 'archived'
          || vm.folder === 'binned'
        }
        activeScope={vm.activeScope}
        scopeCounts={scopeCounts}
        rightTab={vm.rightTab}
        onRightTab={vm.setRightTab}
        selectedEmailId={vm.selectedEmailId}
        folderAction={<RestoreAllAction vm={vm} />}
      />

      {vm.rightTab === 'inbox' && (
        <>
          <div className="mt-1.5">
            {vm.selectedIds.size > 0 ? (
              <BulkCommandRow vm={vm} />
            ) : (
              <SearchCommandRow vm={vm} />
            )}
          </div>
          <InboxFilterPills
            pills={vm.filterPills}
            onRemove={vm.removeFilterPill}
            onClearAll={vm.clearAllFilters}
          />
        </>
      )}
    </div>
  )
}
